import {
  OfferCategory,
  OfferObjective,
  OfferCtaSubcategory,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { PlayType } from '@bbkAdminRedux/rtkq/play.slice';
import { type CtaDetails } from '@bbkAdminUtils/api-client/rest-modals-cards-types';

export interface CategorizedOffer {
  offer: RestOffer;
  isMatchingObjective: boolean;
  targetObjective?: OfferObjective;
}

export interface OfferCategorizationResult {
  matchingOffers: RestOffer[];
  nonMatchingOffers: CategorizedOffer[];
  hasMatchingOffers: boolean;
}

const PLAY_TYPE_TO_OBJECTIVE_MAP: Partial<Record<PlayType, OfferObjective[]>> =
  {
    [PlayType.ACQUISITION]: [OfferObjective.acquisition],
    [PlayType.UPSELL]: [OfferObjective.upsell],
    [PlayType.RETENTION]: [OfferObjective.retention],
  };

const PLAY_TYPE_TO_CROSS_OBJECTIVE_RULES: Partial<
  Record<
    PlayType,
    Array<{
      category: OfferCategory;
      subcategory?: OfferCtaSubcategory | OfferCtaSubcategory[];
      requiredObjective: OfferObjective;
    }>
  >
> = {
  [PlayType.ACQUISITION]: [
    {
      category: OfferCategory.extension,
      subcategory: OfferCtaSubcategory.extend_trial,
      requiredObjective: OfferObjective.retention,
    },
  ],
  [PlayType.UPSELL]: [
    {
      category: OfferCategory.plan_change,
      subcategory: [
        OfferCtaSubcategory.upgrade,
        OfferCtaSubcategory.downgrade,
        OfferCtaSubcategory.swap_product,
        OfferCtaSubcategory.add_on,
      ],
      requiredObjective: OfferObjective.retention,
    },
    {
      category: OfferCategory.multi_action,
      requiredObjective: OfferObjective.retention,
    },
  ],
  [PlayType.RETENTION]: [
    {
      category: OfferCategory.extension,
      subcategory: OfferCtaSubcategory.extend_trial,
      requiredObjective: OfferObjective.acquisition,
    },
    {
      category: OfferCategory.plan_change,
      subcategory: [
        OfferCtaSubcategory.upgrade,
        OfferCtaSubcategory.downgrade,
        OfferCtaSubcategory.swap_product,
        OfferCtaSubcategory.add_on,
      ],
      requiredObjective: OfferObjective.upsell,
    },
    {
      category: OfferCategory.multi_action,
      requiredObjective: OfferObjective.upsell,
    },
  ],
};

const getOfferSubcategory = (
  offer: RestOffer
): OfferCtaSubcategory | undefined => {
  const firstCard = offer.cards?.[0];
  if (firstCard && firstCard.requires && 'forwards' in firstCard.requires) {
    const forwards = firstCard.requires.forwards;
    if (forwards && 'cta_details_list' in forwards) {
      const ctaDetailsList = forwards.cta_details_list;
      if (Array.isArray(ctaDetailsList) && ctaDetailsList.length > 0) {
        const firstCta = ctaDetailsList[0] as Partial<CtaDetails>;
        return firstCta?.sub_category as OfferCtaSubcategory | undefined;
      }
    }
  }
  return undefined;
};

export const isOfferMatchingObjective = (
  offer: RestOffer,
  playType: PlayType
): boolean => {
  const expectedObjectives = PLAY_TYPE_TO_OBJECTIVE_MAP[playType];
  if (!expectedObjectives || !offer.objective) return false;

  return expectedObjectives.includes(offer.objective);
};

export const isOfferCrossObjective = (
  offer: RestOffer,
  playType: PlayType
): boolean => {
  const crossOfferObjectiveRules = PLAY_TYPE_TO_CROSS_OBJECTIVE_RULES[playType];

  if (!crossOfferObjectiveRules || !offer.category || !offer.objective)
    return false;

  if (offer.category === OfferCategory.multi_cta) return false;

  const offerSubcategory = getOfferSubcategory(offer);

  return crossOfferObjectiveRules.some((rule) => {
    if (offer.objective !== rule.requiredObjective) return false;

    if (offer.category !== rule.category) return false;

    if (rule.subcategory) {
      if (Array.isArray(rule.subcategory)) {
        return (
          offerSubcategory !== undefined &&
          rule.subcategory.includes(offerSubcategory)
        );
      }
      return offerSubcategory === rule.subcategory;
    }

    return true;
  });
};

export const getTargetObjectiveForPlayType = (
  playType: PlayType
): OfferObjective => {
  const objectives = PLAY_TYPE_TO_OBJECTIVE_MAP[playType];
  return objectives?.[0] as OfferObjective;
};

export const categorizeOffersForPlay = (
  offers: RestOffer[],
  playType: Exclude<PlayType, PlayType.CHURN_DEFLECT>,
  offerPresentation?: string
): OfferCategorizationResult => {
  const filteredOffers = offerPresentation
    ? offers.filter(
        (offer) =>
          offer.presentation === offerPresentation && offer.active === true
      )
    : offers.filter((offer) => offer.active === true);
  const matchingOffers: RestOffer[] = [];
  const nonMatchingOffers: CategorizedOffer[] = [];

  const targetObjective = getTargetObjectiveForPlayType(playType);

  filteredOffers.forEach((offer) => {
    if (isOfferMatchingObjective(offer, playType)) {
      matchingOffers.push(offer);
    } else if (isOfferCrossObjective(offer, playType)) {
      nonMatchingOffers.push({
        offer,
        isMatchingObjective: false,
        targetObjective,
      });
    }
  });

  return {
    matchingOffers,
    nonMatchingOffers,
    hasMatchingOffers: matchingOffers.length > 0,
  };
};
